import React, { useMemo } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  InputNumber,
  Row,
  Space,
  Tag,
  Typography,
  Badge,
  Ribbon,
  Alert,
} from "antd";
import { Icon } from "@alpaca/framework";
import {
  derivePlanDisplayData,
  toPositiveInteger,
} from "../../../../../helpers/billingHelpers";
import "./../billingSettings.styles.scss";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const MIN_USERS = 1;

const PlanSelection = ({
  planOptions,
  userCount,
  onUserCountChange,
  selectedPlanId,
  onPlanHighlight,
  onPlanConfirm,
  formatCurrency,
  billingData = {},
}) => {
  const normalizedUserCount = toPositiveInteger(userCount, MIN_USERS);

  const computedPlans = useMemo(
    () =>
      planOptions.map((plan) => {
        const {
          planId,
          totalAmount,
          pricePerUser,
          currency,
          billingCycleLabel,
          commitmentLabel,
        } = derivePlanDisplayData(plan, normalizedUserCount);

        return {
          plan,
          planId,
          totalAmount,
          pricePerUser,
          currency,
          billingCycleLabel,
          commitmentLabel,
        };
      }),
    [planOptions, normalizedUserCount]
  );

  const handleDecreaseUsers = () => {
    const updatedCount = Math.max(MIN_USERS, normalizedUserCount - 1);
    onUserCountChange(updatedCount);
  };

  const handleIncreaseUsers = () => {
    const updatedCount = normalizedUserCount + 1;
    onUserCountChange(updatedCount);
  };

  const handleInputChange = (value) => {
    const parsedValue = toPositiveInteger(value, MIN_USERS);
    onUserCountChange(parsedValue);
  };

  function determineTrialMessage(billing = billingData) {
    if (billing.type !== "trial") return;

    let expiryDate = new Date(
      new Date().getTime() + billing.days * 24 * 60 * 60 * 1000
    );

    expiryDate = dayjs(expiryDate).format("MMM D, YYYY");

    let messageToDisplay = `Free Trial of Alpaca Software set to expire on ${expiryDate}`;
    if (billing.days < 0)
      messageToDisplay = `Your free trial has expired. Contact our sales team to extend your trial or subscribe. If no action is taken, this account may be deactivated.`;

    return (
      <Alert
        style={{ width: "60%", marginLeft: "20%", textAlign: "center" }}
        message={
          <div>
            {messageToDisplay}
            <br></br>
            <Button>Contact Sales</Button>
          </div>
        }
      ></Alert>
    );
  }

  return (
    <div className="billing-plan-selection">
      <div className="head-btn-subheader">
        <h2 className="sectionHeading">Billing</h2>
        {/* <p className="bodyTextSmall">
          Enter your total number of employees, then select a billing plan.
        </p> */}
        <p className="bodyTextSmall">
          Select a billing plan to start using the software.
        </p>
      </div>
      {/* <div className="plan-counter">
        <p className="user-text-title" strong>
          How many employees?
        </p>

        <Space align="center">
          <Button onClick={handleDecreaseUsers} className="change-user-btn">
            <Icon icon="minus" />
          </Button>
          <InputNumber
            min={MIN_USERS}
            value={normalizedUserCount}
            onChange={handleInputChange}
            className="user-number-input"
            controls={false}
          />
          <Button onClick={handleIncreaseUsers} className="change-user-btn">
            <Icon icon="plus" />
          </Button>
        </Space>
        {normalizedUserCount > 0 && (
          <div className="billing-user-container">
            {Array.from({ length: Math.min(normalizedUserCount, 10) }).map(
              (_, i) => (
                <div key={i}>
                  <Icon icon="user" className="billing-icon" />
                </div>
              )
            )}
          </div>
        )}
      </div> */}
      {determineTrialMessage()}

      <Row gutter={[24, 24]} className="billing-plan-cards">
        {computedPlans.map(
          (
            {
              plan,
              planId,
              totalAmount,
              pricePerUser,
              currency,
              billingCycleLabel,
              commitmentLabel,
            },
            index // <-- this is the index of the current item
          ) => {
            const isSelected = selectedPlanId === planId;
            const cardClass = `billing-class-${index + 1}`; // class-1, class-2, class-3

            return (
              <Col xs={24} md={8} key={planId || plan.name}>
                <Badge.Ribbon
                  text={commitmentLabel}
                  color="cyan"
                  className={`billing-badge ${cardClass}`}
                >
                  <Card
                    hoverable
                    className={`billing-plan-card ${cardClass} ${
                      isSelected ? "billing-plan-card--selected" : ""
                    }`}
                    onClick={() => planId && onPlanHighlight(planId)}
                  >
                    <div className="billing-plan-content">
                      <h1 level={4} className="billing-plan-title">
                        {plan.name}
                      </h1>

                      {/* <p className="employee-text" strong>
                        {billingCycleLabel === "year"
                          ? formatCurrency(pricePerUser / 12, currency)
                          : formatCurrency(pricePerUser, currency)}
                        {billingCycleLabel === "year"
                          ? " per employee per month"
                          : " per employee"}
                      </p> */}
                      <Text type="secondary" className="billing-subtitle-text">
                        {plan.description}
                      </Text>
                      <Divider />
                      <div className="billing-price-container">
                        <p>Total:</p>
                        <div>
                          <h1>{formatCurrency(totalAmount, currency)}</h1>
                          <span>/ {billingCycleLabel}</span>
                        </div>
                      </div>
                      <Button
                        type="primary"
                        size="large"
                        block
                        onClick={(event) => {
                          event.stopPropagation();
                          if (planId) {
                            onPlanConfirm(planId);
                          }
                        }}
                      >
                        Get Started
                      </Button>
                    </div>
                  </Card>
                </Badge.Ribbon>
              </Col>
            );
          }
        )}
      </Row>
    </div>
  );
};

PlanSelection.defaultProps = {
  planOptions: [],
  onPlanHighlight: () => {},
  onPlanConfirm: () => {},
};

export default PlanSelection;
